import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Tag, Power, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { fmtPrice } from "@/lib/company";
import VehicleForm from "@/components/VehicleForm";
import { VehicleImage } from "@/components/VehicleImage";

const Badge = ({ children, tone = "muted", testId }) => {
  const tones = {
    muted: "bg-elevated text-dim border-line",
    red: "bg-brand/15 text-red-700 border-brand/40",
    green: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    amber: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  };
  return <span data-testid={testId} className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
};

const btn = "btn-pill inline-flex items-center gap-1.5 border border-line text-ink px-3.5 py-2 text-xs font-semibold hover:border-silver transition-colors";

const Row = ({ v, onEdit, onAction, confirmDelete }) => {
  const sources = [...new Set((v.source_refs || []).map((r) => r.label))];
  return (
    <div data-testid={`admin-vehicle-${v.id}`} className="rounded-xl border border-line bg-surface p-4 flex flex-col md:flex-row md:items-center gap-4">
      <div className="relative w-28 h-[72px] rounded-md overflow-hidden bg-deep shrink-0"><VehicleImage vehicle={v} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-sm text-ink truncate">{v.title}</p>
          {v.sold ? <Badge tone="red">Verkauft</Badge> : v.status === "inactive" ? <Badge tone="amber" testId={`admin-vehicle-inactive-${v.id}`}>Inaktiv</Badge> : <Badge tone="green">Online</Badge>}
          {v.locked_fields?.length > 0 && <Badge><Lock size={9} className="inline -mt-0.5" /> {v.locked_fields.length} manuell</Badge>}
        </div>
        <p className="text-xs text-faint mt-1">{v.brand} · {fmtPrice(v.price)} · {(v.mileage || 0).toLocaleString("de-AT")} km{v.power_ps ? ` · ${v.power_ps} PS` : ""}</p>
        <p className="text-[11px] text-faint mt-1">
          Quelle: {sources.length ? sources.join(", ") : "Manuell angelegt"}{v.inactive_reason && v.status === "inactive" ? ` · ${v.inactive_reason}` : ""}
        </p>
      </div>
      <div className="flex gap-2 shrink-0 flex-wrap">
        <button data-testid={`admin-edit-vehicle-${v.id}`} onClick={() => onEdit(v)} className={btn}><Pencil size={12} /> Bearbeiten</button>
        <button data-testid={`admin-active-vehicle-${v.id}`} onClick={() => onAction(v, "toggle-active")} className={btn}>
          <Power size={12} /> {v.status === "inactive" ? "Aktivieren" : "Deaktivieren"}
        </button>
        <button data-testid={`admin-sold-vehicle-${v.id}`} onClick={() => onAction(v, "toggle-sold")} className={btn}>
          <Tag size={12} /> {v.sold ? "Wieder verfügbar" : "Verkauft"}
        </button>
        <button data-testid={confirmDelete ? "confirm-delete-vehicle-btn" : `admin-delete-vehicle-${v.id}`} onClick={() => onAction(v, "delete")}
          className={`btn-pill inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold transition-colors ${confirmDelete ? "bg-brand text-brand-ink" : "border border-brand/40 text-red-700 hover:bg-brand/10"}`}>
          <Trash2 size={12} /> {confirmDelete ? "Wirklich löschen?" : "Löschen"}
        </button>
      </div>
    </div>
  );
};

export const AdminVehicles = ({ vehicles, reload }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const save = async (data) => {
    setSaving(true);
    try {
      if (edit) await api.put(`/admin/vehicles/${edit.id}`, data);
      else await api.post("/admin/vehicles", data);
      toast.success("Fahrzeug gespeichert");
      setFormOpen(false);
      setEdit(null);
      reload();
    } catch {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  const action = async (v, kind) => {
    if (kind === "delete" && confirmId !== v.id) {
      setConfirmId(v.id);
      setTimeout(() => setConfirmId((c) => (c === v.id ? null : c)), 4000);
      return;
    }
    try {
      if (kind === "delete") await api.delete(`/admin/vehicles/${v.id}`);
      else await api.post(`/admin/vehicles/${v.id}/${kind}`);
      toast.success(kind === "delete" ? "Fahrzeug gelöscht" : "Gespeichert");
      setConfirmId(null);
      reload();
    } catch {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  return (
    <div data-testid="admin-vehicles-list">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-sm text-dim">Manuelle Änderungen an synchronisierten Fahrzeugen bleiben beim nächsten Abgleich erhalten.</p>
        <button data-testid="admin-new-vehicle-btn" onClick={() => { setEdit(null); setFormOpen(true); }}
          className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink hover:bg-action-hover px-5 py-2.5 text-sm font-semibold">
          <Plus size={15} /> Neues Fahrzeug
        </button>
      </div>
      <div className="space-y-3">
        {vehicles.map((v) => (
          <Row key={v.id} v={v} confirmDelete={confirmId === v.id} onAction={action} onEdit={(x) => { setEdit(x); setFormOpen(true); }} />
        ))}
        {vehicles.length === 0 && <p className="text-faint text-sm py-10 text-center">Noch keine Fahrzeuge – starten Sie einen Abgleich oder importieren Sie eine Datei.</p>}
      </div>
      <VehicleForm open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEdit(null); }} vehicle={edit} onSave={save} saving={saving} />
    </div>
  );
};

export default AdminVehicles;
